import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import { AppState } from '../store';
import moment from 'moment';
import { EmployeeLeavingRequestDTO } from '@iota/inh-core-v1-management-client';
import {
    CancelEmployeeLeaving,
    CancelEmployeeLeavingFailure,
    CancelEmployeeLeavingSuccess,
    LoadAllEmployeeLeavingRequestsByCompany,
    LoadAllEmployeeLeavingRequestsByCompanyFailure,
    LoadAllEmployeeLeavingRequestsByCompanySuccess,
    ScheduleEmployeeLeaving,
    ScheduleEmployeeLeavingFailure,
    ScheduleEmployeeLeavingSuccess,
    UpdateEmployeeLeavingDate,
    UpdateEmployeeLeavingDateFailure,
    UpdateEmployeeLeavingDateSuccess
} from '../actions/employee-leaving.actions';

export interface EmployeeLeavingState extends EntityState<EmployeeLeavingRequestDTO> {
    loading: boolean;
    actionLoading: boolean;
    actionError: any;
    error: any;
}

function selectId(a: EmployeeLeavingRequestDTO): string {
    return a.id;
}

function sortComparer(a: EmployeeLeavingRequestDTO, b: EmployeeLeavingRequestDTO): number {
    return moment(b.licenseEndDate).toDate().getTime() - moment(a.licenseEndDate).toDate().getTime();
}

export const employeeLeavingAdapter: EntityAdapter<EmployeeLeavingRequestDTO> = createEntityAdapter<EmployeeLeavingRequestDTO>({
    selectId,
    sortComparer
});

export const initialState: EmployeeLeavingState = employeeLeavingAdapter.getInitialState({
    loading: false,
    actionLoading: false,
    actionError: undefined,
    error: undefined
});

export const employeeLeavingReducer = createReducer(
    initialState,
    on(LoadAllEmployeeLeavingRequestsByCompany, (state) => ({
        ...state,
        error: undefined,
        loading: true
    })),
    on(LoadAllEmployeeLeavingRequestsByCompanySuccess, (state, { employeeLeavingRequests }) =>
        employeeLeavingAdapter.upsertMany(Array.isArray(employeeLeavingRequests) ? employeeLeavingRequests : [], {
            ...state,
            loading: false
        })
    ),
    on(LoadAllEmployeeLeavingRequestsByCompanyFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(ScheduleEmployeeLeaving, UpdateEmployeeLeavingDate, CancelEmployeeLeaving, (state) => ({
        ...state,
        actionError: undefined,
        actionLoading: true
    })),

    on(ScheduleEmployeeLeavingFailure, UpdateEmployeeLeavingDateFailure, CancelEmployeeLeavingFailure, (state, { error }) => ({
        ...state,
        actionError: error,
        actionLoading: false
    })),

    on(
        ScheduleEmployeeLeavingSuccess,
        UpdateEmployeeLeavingDateSuccess,
        CancelEmployeeLeavingSuccess,
        (state, { employeeLeavingRequest }) => employeeLeavingAdapter.upsertOne(employeeLeavingRequest, { ...state, actionLoading: false })
    )
);

const { selectAll } = employeeLeavingAdapter.getSelectors();

export const employeeLeavingState = (state: AppState) => state.employeeLeavingState;
export const allEmployeeLeavingRequests = createSelector(employeeLeavingState, selectAll);
export const employeeLeavingRequestsLoading = createSelector(employeeLeavingState, (state) => state.loading);
export const employeeLeavingActionLoading = createSelector(employeeLeavingState, (state) => state.actionLoading);
export const employeeLeavingActionError = createSelector(employeeLeavingState, (state) => state.actionError);

export const employeeLeavingRequestById = (id: string) =>
    createSelector(allEmployeeLeavingRequests, (requests) => requests?.find((request) => request.id === id));
